// CaptainPaws.js
import React, { useState, useEffect, useCallback, useRef } from 'react';

// --- Configuration ---
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const PLANE_START_Y_PERCENT = 50;
const PLANE_SPEED_VERTICAL = 4;
const OBJECT_BASE_SPEED = 3.0; // Increased base speed slightly for more motion feel
const STAR_SPAWN_RATE = 1500; // Spawn stars a bit faster
const CLOUD_SPAWN_RATE = 2500;
const TREAT_SPAWN_RATE = 4500;
const DISTANT_CLOUD_SPAWN_RATE = 6000; // Slower spawn for background elements
const STAR_POINTS = 10;
const TREAT_POINTS = 50;

// Scaling constants for forward motion effect
const MIN_OBJECT_SCALE = 0.3; // Objects start at 30% size in the distance
const FULL_SCALE_DISTANCE_FACTOR = 0.4; // Objects reach full scale 40% of the way across the screen

// --- Helper Functions ---
const getRandomInt = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
};
const getRandomFloat = (min, max) => {
    return Math.random() * (max - min) + min;
};


function CaptainPaws() {
    // --- State ---
    const [planeY, setPlaneY] = useState((GAME_HEIGHT * PLANE_START_Y_PERCENT) / 100);
    const [verticalVelocity, setVerticalVelocity] = useState(0);
    const [keysPressed, setKeysPressed] = useState({});
    const [stars, setStars] = useState([]);
    const [clouds, setClouds] = useState([]);
    const [treats, setTreats] = useState([]);
    const [distantClouds, setDistantClouds] = useState([]); // Added state for parallax
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const gameAreaRef = useRef(null);
    const gameLoopId = useRef(null);
    const lastStarSpawn = useRef(0);
    const lastCloudSpawn = useRef(0);
    const lastTreatSpawn = useRef(0);
    const lastDistantCloudSpawn = useRef(0); // Added timer ref
    const lastFrameTime = useRef(0);

    // --- Plane Boundaries ---
    const MIN_PLANE_Y = 40;
    const MAX_PLANE_Y = GAME_HEIGHT - 100;

    // --- Calculate Scale based on X (Distance) ---
    const calculateDynamicScale = (x) => {
        // Calculate the progress across the screen (0 at right edge, 1 at left edge)
        const progress = Math.max(0, Math.min(1, (GAME_WIDTH - x) / GAME_WIDTH));
        // Scale from MIN_OBJECT_SCALE to 1.0 based on progress, reaching 1.0 early
        const scaleFactor = progress / FULL_SCALE_DISTANCE_FACTOR;
        const scale = MIN_OBJECT_SCALE + (1.0 - MIN_OBJECT_SCALE) * Math.min(1.0, scaleFactor);
        return scale;
    };
     // --- Calculate Opacity based on X (Distance) ---
    const calculateDynamicOpacity = (x) => {
        const progress = Math.max(0, Math.min(1, (GAME_WIDTH - x) / GAME_WIDTH));
        // Opacity ramps up from 0.6 to 1.0
        return 0.6 + 0.4 * Math.min(1.0, progress / (FULL_SCALE_DISTANCE_FACTOR * 1.5));
    };


    // --- Game Loop ---
    const gameLoop = useCallback((currentTime) => {
        if (!gameStarted || gameOver) {
            lastFrameTime.current = currentTime; // Keep updating time even when paused/over
            gameLoopId.current = requestAnimationFrame(gameLoop);
            return;
        }

        // Calculate delta time for smoother animations/physics
        const deltaTime = (currentTime - lastFrameTime.current) || (1000 / 60); // Fallback to 60fps if first frame
        const timeScale = deltaTime / (1000 / 60); // Normalize speed based on 60fps target
        lastFrameTime.current = currentTime;

        // --- 1. Handle Input & Update Velocity ---
        let targetVelocity = 0;
        if (keysPressed['ArrowUp'] || keysPressed['w']) {
            targetVelocity = -PLANE_SPEED_VERTICAL;
        } else if (keysPressed['ArrowDown'] || keysPressed['s']) {
            targetVelocity = PLANE_SPEED_VERTICAL;
        }

        // Simple acceleration/deceleration towards target velocity
        const acceleration = 0.5 * timeScale;
        if (verticalVelocity < targetVelocity) {
            setVerticalVelocity(v => Math.min(v + acceleration, targetVelocity));
        } else if (verticalVelocity > targetVelocity) {
            setVerticalVelocity(v => Math.max(v - acceleration, targetVelocity));
        }

        // --- 2. Move Plane ---
        setPlaneY(prevY => {
            const nextY = prevY + verticalVelocity * timeScale;
            // Clamp plane position within bounds
            return Math.max(MIN_PLANE_Y, Math.min(MAX_PLANE_Y, nextY));
        });

        // --- 3. Spawn Objects (Time-based) ---
        // Spawn Stars
        if (currentTime - lastStarSpawn.current > STAR_SPAWN_RATE) {
            setStars(prev => [...prev, {
                id: `star-${Date.now()}`,
                x: GAME_WIDTH + 30,
                y: getRandomInt(MIN_PLANE_Y, MAX_PLANE_Y),
                type: 'star'
            }]);
            lastStarSpawn.current = currentTime;
        }
        // Spawn Treats
         if (currentTime - lastTreatSpawn.current > TREAT_SPAWN_RATE) {
            setTreats(prev => [...prev, {
                id: `treat-${Date.now()}`,
                x: GAME_WIDTH + 50,
                y: getRandomInt(MIN_PLANE_Y + 20, MAX_PLANE_Y - 20),
                type: 'treat'
            }]);
            lastTreatSpawn.current = currentTime;
        }
        // Spawn Clouds (Foreground)
         if (currentTime - lastCloudSpawn.current > CLOUD_SPAWN_RATE) {
             // Give foreground clouds slightly more speed variation
            const speedMultiplier = getRandomFloat(0.9, 1.4);
            setClouds(prev => [...prev, {
                id: `cloud-${Date.now()}`,
                x: GAME_WIDTH + 100,
                y: getRandomInt(MIN_PLANE_Y - 30, MAX_PLANE_Y + 30), // Can overlap vertically a bit more
                type: 'cloud',
                speedMultiplier: speedMultiplier
            }]);
            lastCloudSpawn.current = currentTime;
        }
        // Spawn Distant Clouds (Background - PARALLAX)
        if (currentTime - lastDistantCloudSpawn.current > DISTANT_CLOUD_SPAWN_RATE) {
            setDistantClouds(prev => [...prev, {
                id: `dcloud-${Date.now()}`,
                x: GAME_WIDTH + 150, // Spawn further back
                y: getRandomInt(0, GAME_HEIGHT * 0.7), // Tend to be higher up
                type: 'distant_cloud',
                // Much slower speed for parallax
                speedMultiplier: getRandomFloat(0.2, 0.4)
            }]);
            lastDistantCloudSpawn.current = currentTime;
        }


        // --- 4. Move Objects & Check Collisions ---
        const planeHitbox = { x: 80, y: planeY, width: 60, height: 40 }; // Approximate cockpit/nose area for collision

        const checkCollision = (obj, hitbox) => (
            planeHitbox.x < hitbox.x + hitbox.width &&
            planeHitbox.x + planeHitbox.width > hitbox.x &&
            planeHitbox.y < hitbox.y + hitbox.height &&
            planeHitbox.y + planeHitbox.height > hitbox.y
        );

        // Move Stars & Check Collection
        setStars(prevStars =>
            prevStars
                .map(star => ({
                    ...star,
                    x: star.x - OBJECT_BASE_SPEED * 1.0 * timeScale // Stars move at base speed
                 }))
                .filter(star => {
                    // Use scaled hitbox for collision detection
                    const scale = calculateDynamicScale(star.x);
                    const starHitbox = { x: star.x, y: star.y, width: 25 * scale, height: 25 * scale };
                    if (scale > 0.5 && checkCollision(star, starHitbox)) { // Only check collision when reasonably close/large
                        setScore(s => s + STAR_POINTS);
                        return false; // Remove collected star
                    }
                    return star.x > -40 * scale; // Keep on screen slightly longer if small
                })
        );

        // Move Treats & Check Collection
        setTreats(prevTreats =>
            prevTreats
                .map(treat => ({
                    ...treat,
                    x: treat.x - OBJECT_BASE_SPEED * 1.1 * timeScale // Treats slightly faster
                }))
                .filter(treat => {
                    const scale = calculateDynamicScale(treat.x);
                    const treatHitbox = { x: treat.x, y: treat.y, width: 30 * scale, height: 30 * scale };
                    if (scale > 0.5 && checkCollision(treat, treatHitbox)) {
                        setScore(s => s + TREAT_POINTS);
                        return false; // Remove collected treat
                    }
                    return treat.x > -50 * scale;
                })
        );

        // Move Clouds (Foreground) & Check Collision (Game Over)
        setClouds(prevClouds =>
            prevClouds
                .map(cloud => ({
                    ...cloud,
                    x: cloud.x - OBJECT_BASE_SPEED * cloud.speedMultiplier * timeScale
                }))
                .filter(cloud => {
                    const scale = calculateDynamicScale(cloud.x);
                    // Approximate hitbox for the scaled cloud SVG group
                    const cloudHitbox = { x: cloud.x + 10 * scale, y: cloud.y + 5 * scale, width: 80 * scale, height: 40 * scale };
                     if (scale > 0.6 && checkCollision(cloud, cloudHitbox)) { // Check collision when larger
                        setGameOver(true); // Game over on cloud collision!
                        setVerticalVelocity(0); // Stop plane movement
                        return true; // Keep the cloud that caused the crash visible briefly
                    }
                    return cloud.x > -120 * scale; // Keep until off-screen
                })
        );

         // Move Distant Clouds (PARALLAX) - No collision
        setDistantClouds(prevDistantClouds =>
            prevDistantClouds
                .map(dCloud => ({
                    ...dCloud,
                    x: dCloud.x - OBJECT_BASE_SPEED * dCloud.speedMultiplier * timeScale
                 }))
                .filter(dCloud => {
                    const scale = calculateDynamicScale(dCloud.x); // Also scale distant clouds
                    return dCloud.x > -150 * scale; // Keep until off-screen
                })
        );


        // --- 5. Request Next Frame ---
        gameLoopId.current = requestAnimationFrame(gameLoop);

    }, [gameStarted, gameOver, keysPressed, planeY, verticalVelocity]); // Dependencies

    // --- Keyboard Input Handlers ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Prevent default scrolling for arrow keys and space
             if (['ArrowUp', 'ArrowDown', ' ', 'w', 's'].includes(e.key)) {
                e.preventDefault();
            }
             // Use space to start/restart game
            if (!gameStarted && e.key === ' ') {
                startGame();
                return;
            }
            if (gameOver && e.key === ' ') {
                 restartGame();
                 return;
            }
            // Only process movement keys if game is active
            if(gameStarted && !gameOver) {
                setKeysPressed(prev => ({ ...prev, [e.key]: true }));
            }
        };
        const handleKeyUp = (e) => {
            // Always allow key up to register to prevent stuck keys
            setKeysPressed(prev => ({ ...prev, [e.key]: false }));
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        // Cleanup function
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [gameStarted, gameOver]); // Rerun effect if gameStarted/gameOver changes to handle spacebar correctly

    // --- Start/Stop Game Loop ---
    useEffect(() => {
        if (gameStarted && !gameOver) {
            // Reset timer and start loop when game starts or restarts
            lastFrameTime.current = performance.now();
            gameLoopId.current = requestAnimationFrame(gameLoop);
        } else if (gameLoopId.current) {
             // Cleanup (cancel loop) if game stops or is over
            cancelAnimationFrame(gameLoopId.current);
            gameLoopId.current = null; // Clear the ref
        }

        // Ensure focus when game starts/restarts for keyboard input
        if(gameStarted && gameAreaRef.current){
            gameAreaRef.current.focus();
        }

        // Cleanup on unmount
        return () => {
            if (gameLoopId.current) {
                cancelAnimationFrame(gameLoopId.current);
            }
        };
    }, [gameStarted, gameOver, gameLoop]); // Rerun effect if state changes or gameLoop function identity changes


    // --- Game State Functions ---
     const startGame = () => {
        setScore(0);
        setPlaneY((GAME_HEIGHT * PLANE_START_Y_PERCENT) / 100);
        setVerticalVelocity(0);
        setStars([]);
        setClouds([]);
        setTreats([]);
        setDistantClouds([]); // Reset distant clouds
        setKeysPressed({});
        setGameOver(false);
        setGameStarted(true); // Set this last to trigger useEffects
        lastStarSpawn.current = performance.now(); // Reset spawn timers
        lastCloudSpawn.current = performance.now();
        lastTreatSpawn.current = performance.now();
        lastDistantCloudSpawn.current = performance.now(); // Reset distant cloud timer
    };

     const restartGame = () => {
        // Reset everything to initial state, effectively same as startGame
        startGame();
    };

    // --- Inline Styles ---
    const gameAreaStyle = {
        width: `${GAME_WIDTH}px`,
        height: `${GAME_HEIGHT}px`,
        backgroundImage: `linear-gradient(to bottom, #45aaf2 0%, #87CEEB 60%, #a5d6a7 60%, #81c784 100%)`,
        overflow: 'hidden',
        position: 'relative', // Needed for absolute positioning of elements
        cursor: 'none',
        border: '3px solid #777',
        borderRadius: '10px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
        margin: '20px auto',
        // Use Comic Sans MS, fallback to cursive, then sans-serif
        fontFamily: `'Comic Sans MS', cursive, sans-serif`,
        outline: 'none', // Remove focus outline
    };

    const scoreStyle = {
        position: 'absolute',
        top: '15px',
        left: '20px',
        fontSize: '28px',
        fontWeight: 'bold',
        color: 'white',
        textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
        zIndex: 10,
        userSelect: 'none',
    };

     const altitudeStyle = {
        position: 'absolute',
        top: '15px',
        right: '20px',
        fontSize: '20px',
        color: 'white',
        textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
        zIndex: 10,
        userSelect: 'none',
    };

    const messageStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '48px',
        color: '#ff4757', // Bright red/pink
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        padding: '30px 40px',
        borderRadius: '15px',
        textAlign: 'center',
        zIndex: 20,
        textShadow: '3px 3px 6px rgba(0,0,0,0.3)',
        border: '3px solid #ff6b81',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
    };

     const subMessageStyle = {
        fontSize: '24px',
        color: '#576574',
        marginTop: '15px',
    };

    // --- SVG Elements ---

    // Cockpit SVG Overlay
    const svgCockpit = (
        <svg
            style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '180px',
                pointerEvents: 'none',
                zIndex: 5
             }}
            viewBox={`0 0 ${GAME_WIDTH} 180`} preserveAspectRatio="none"
        >
            {/* Main Dashboard Panel */}
            <defs>
                <linearGradient id="dashGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#8d6e63', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#5d4037', stopOpacity: 1 }} />
                </linearGradient>
                <filter id="glow">
                    <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                    <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
                <radialGradient id="pawGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                    <stop offset="0%" style={{stopColor:'#ffcc80',stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#ffa726',stopOpacity:1}} />
                </radialGradient>
                 <radialGradient id="cloudGradient" cx="50%" cy="50%" r="70%" fx="50%" fy="50%">
                    <stop offset="0%" style={{stopColor:'white', stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#f1f1f1', stopOpacity:1}} />
                </radialGradient>
            </defs>
            <path d={`M 0 180 L 0 60 Q ${GAME_WIDTH * 0.1} 40 ${GAME_WIDTH * 0.2} 40 T ${GAME_WIDTH * 0.8} 40 Q ${GAME_WIDTH * 0.9} 40 ${GAME_WIDTH} 60 L ${GAME_WIDTH} 180 Z`}
                  fill="url(#dashGradient)" stroke="#4e342e" strokeWidth="4" />

            {/* Windscreen Frame */}
            <path d={`M 40 170 L 40 70 Q ${GAME_WIDTH / 2} 10 ${GAME_WIDTH - 40} 70 L ${GAME_WIDTH - 40} 170`}
                  fill="none" stroke="#a1887f" strokeWidth="15" />
             <path d={`M 50 165 L 50 75 Q ${GAME_WIDTH / 2} 20 ${GAME_WIDTH - 50} 75 L ${GAME_WIDTH - 50} 165`}
                  fill="none" stroke="#5d4037" strokeWidth="5" />

            {/* Simple Gauges */}
            <circle cx={GAME_WIDTH * 0.3} cy="80" r="25" fill="#e0e0e0" stroke="#424242" strokeWidth="2"/>
            <line x1={GAME_WIDTH * 0.3} y1="80" x2={GAME_WIDTH * 0.3 + 15} y2="70" stroke="#f44336" strokeWidth="3" />
            <circle cx={GAME_WIDTH * 0.7} cy="80" r="25" fill="#e0e0e0" stroke="#424242" strokeWidth="2"/>
            <line x1={GAME_WIDTH * 0.7} y1="80" x2={GAME_WIDTH * 0.7 - 10} y2="65" stroke="#2196F3" strokeWidth="3" />

            {/* Captain Paws Pilot */}
             <g transform="translate(80, 110) scale(0.8)">
                 <circle cx="0" cy="0" r="25" fill="#f5deb3" /> {/* Head - Wheat */}
                 <circle cx="-15" cy="-15" r="10" fill="#d2b48c" /> {/* Ear Left - Tan */}
                 <circle cx="15" cy="-15" r="10" fill="#d2b48c" /> {/* Ear Right */}
                 <ellipse cx="-8" cy="0" rx="3" ry="4" fill="#212121" /> {/* Eye Left */}
                 <ellipse cx="8" cy="0" rx="3" ry="4" fill="#212121" /> {/* Eye Right */}
                 <ellipse cx="0" cy="10" rx="5" ry="3" fill="#d2b48c" /> {/* Snout */}
                 <circle cx="0" cy="8" r="2" fill="#424242" /> {/* Nose */}
                 <path d="M -5 13 Q 0 16 5 13" stroke="#424242" strokeWidth="1.5" fill="none" /> {/* Mouth */}
                 {/* Tiny Pilot Hat */}
                 <path d="M -15 -25 Q 0 -40 15 -25 L 10 -23 Q 0 -30 -10 -23 Z" fill="#3f51b5"/>
             </g>

             {/* Wing/Nose Hints (subtler) */}
             <path d={`M 0 180 L 0 150 C 50 160 100 180 150 180 Z`} fill="#bdbdbd" opacity="0.7"/>
             <path d={`M ${GAME_WIDTH} 180 L ${GAME_WIDTH} 150 C ${GAME_WIDTH-50} 160 ${GAME_WIDTH-100} 180 ${GAME_WIDTH-150} 180 Z`} fill="#bdbdbd" opacity="0.7"/>
        </svg>
    );

    // Star Component (Apply dynamic scale and opacity)
    const Star = ({ star }) => {
        const scale = calculateDynamicScale(star.x);
        const opacity = calculateDynamicOpacity(star.x); // Add fading effect
        return (
            <svg
                key={star.id}
                style={{
                    position: 'absolute',
                    left: `${star.x}px`,
                    top: `${star.y}px`,
                    width: '25px', // Base width
                    height: '25px', // Base height
                    pointerEvents: 'none',
                    filter: 'url(#glow)',
                    transform: `scale(${scale})`, // Apply dynamic scale
                    opacity: opacity, // Apply dynamic opacity
                    zIndex: 1,
                }}
                viewBox="0 0 512 512"
            >
                <polygon fill="#FFD700" points="256,0 316.4,180 512,180 364.4,291.6 424.8,471.6 256,360 87.2,471.6 147.6,291.6 0,180 195.6,180"/>
            </svg>
        );
    };

    // Treat Component (Apply dynamic scale and opacity)
    const Treat = ({ treat }) => {
        const scale = calculateDynamicScale(treat.x);
        const opacity = calculateDynamicOpacity(treat.x);
        return (
             <svg
                 key={treat.id}
                 style={{
                    position: 'absolute',
                    left: `${treat.x}px`,
                    top: `${treat.y}px`,
                    width: '30px',
                    height: '30px',
                    pointerEvents: 'none',
                    transform: `rotate(30deg) scale(${scale})`, // Apply dynamic scale
                    opacity: opacity,
                    zIndex: 1,
                 }}
                 viewBox="0 0 100 100"
             >
                {/* Paw print paths/ellipses */}
                <ellipse cx="50" cy="65" rx="25" ry="20" fill="url(#pawGradient)" />
                <ellipse cx="25" cy="35" rx="10" ry="12" transform="rotate(-15 25 35)" fill="url(#pawGradient)" />
                <ellipse cx="45" cy="25" rx="10" ry="12" fill="url(#pawGradient)" />
                <ellipse cx="65" cy="25" rx="10" ry="12" fill="url(#pawGradient)" />
                <ellipse cx="85" cy="35" rx="10" ry="12" transform="rotate(15 85 35)" fill="url(#pawGradient)" />
             </svg>
        );
    };


    // Cloud Component (Foreground - Apply dynamic scale and opacity)
    const Cloud = ({ cloud }) => {
        const scale = calculateDynamicScale(cloud.x);
        // Make foreground clouds fully opaque sooner
        const opacity = Math.min(1.0, calculateDynamicOpacity(cloud.x) * 1.5);
        return (
             <svg
                 key={cloud.id}
                 style={{
                     position: 'absolute',
                     left: `${cloud.x}px`,
                     top: `${cloud.y}px`,
                     width: '100px',
                     height: '60px',
                     pointerEvents: 'none',
                     opacity: opacity * 0.85, // Base opacity + dynamic fade
                     transform: `scale(${scale})`, // Apply dynamic scale based on X
                     // Keep zIndex logic based on Y for overlap, maybe adjust thresholds
                     zIndex: (cloud.y < 80 || cloud.y > GAME_HEIGHT - 200) ? 0 : 6,
                 }}
                 viewBox="0 0 120 60"
             >
                  {/* Cloud ellipses */}
                  <ellipse cx="30" cy="35" rx="25" ry="20" fill="url(#cloudGradient)" />
                  <ellipse cx="60" cy="25" rx="35" ry="25" fill="url(#cloudGradient)" />
                  <ellipse cx="90" cy="35" rx="25" ry="20" fill="url(#cloudGradient)" />
                  <ellipse cx="45" cy="45" rx="15" ry="10" fill="url(#cloudGradient)" />
                  <ellipse cx="75" cy="45" rx="15" ry="10" fill="url(#cloudGradient)" />
             </svg>
        );
    };

    // Distant Cloud Component (Background - Apply dynamic scale & fixed low opacity)
    const DistantCloud = ({ dCloud }) => {
        const scale = calculateDynamicScale(dCloud.x) * 0.8; // Make them generally smaller too
        // Distant clouds have lower base opacity and fade in less strongly
        const opacity = 0.4 + 0.3 * Math.min(1.0, (GAME_WIDTH - dCloud.x) / GAME_WIDTH);
        return (
             <svg
                 key={dCloud.id}
                 style={{
                     position: 'absolute',
                     left: `${dCloud.x}px`,
                     top: `${dCloud.y}px`,
                     width: '120px', // Can be larger base size
                     height: '50px',
                     pointerEvents: 'none',
                     opacity: opacity, // Lower fixed opacity for distance
                     transform: `scale(${scale})`, // Apply dynamic scale
                     zIndex: 0, // Ensure they are behind everything else
                 }}
                 viewBox="0 0 120 50" // Slightly flatter shape maybe
             >
                 {/* Simple shapes for distant clouds */}
                 <ellipse cx="30" cy="30" rx="30" ry="15" fill="#f0f0f0" />
                 <ellipse cx="70" cy="25" rx="40" ry="20" fill="#f5f5f5" />
                 <ellipse cx="100" cy="35" rx="20" ry="15" fill="#e8e8e8" />
             </svg>
        );
    };


    // --- Render ---
    return (
        <div
             ref={gameAreaRef}
             style={gameAreaStyle}
             tabIndex="0" // Make div focusable to receive key events
             >

             {/* Start Screen */}
             {!gameStarted && (
                <div style={messageStyle}>
                     <div>Captain Paws!</div>
                     <div style={subMessageStyle}>Press [SPACE] to fly!</div>
                     <div style={{fontSize: '18px', marginTop: '20px', color: '#333'}}>Use Arrow Up/Down or W/S</div>
                 </div>
             )}

             {/* Game Elements (only render if started) */}
             {gameStarted && (
                 <>
                    {/* Render Distant Clouds first (background layer) */}
                    {distantClouds.map(dCloud => <DistantCloud key={dCloud.id} dCloud={dCloud} />)}

                    {/* Render Stars, Treats, and Clouds (foreground layers) */}
                    {stars.map(star => <Star key={star.id} star={star} />)}
                    {treats.map(treat => <Treat key={treat.id} treat={treat} />)}
                    {clouds.map(cloud => <Cloud key={cloud.id} cloud={cloud} />)}

                    {/* UI Elements on top */}
                    <div style={scoreStyle}>Score: {score}</div>
                    <div style={altitudeStyle}>Alt: {Math.max(0, Math.round((MAX_PLANE_Y - planeY) / 5))}</div>

                    {/* Render Cockpit Overlay last (topmost visual layer) */}
                    {svgCockpit}

                     {/* Game Over Screen (On top of everything except maybe debug info) */}
                     {gameOver && (
                        <div style={messageStyle}>
                             <div>Woof! Cloud Crash!</div>
                             <div style={{...subMessageStyle, fontSize: '28px', color: '#333'}}>Score: {score}</div>
                             <div style={subMessageStyle}>Press [SPACE] to fly again!</div>
                         </div>
                     )}
                 </>
             )}
        </div>
    );
}

export default CaptainPaws;

/*
To Use This:
1.  Save this code as `CaptainPaws.js`.
2.  Import and render it in your main App component (`App.js` or similar).
    ```javascript
    import React from 'react';
    import CaptainPaws from './components/CaptainPaws'; // Adjust path if needed
    import './App.css'; // Your main CSS

    function App() {
      return (
        <div className="App" style={{ textAlign: 'center' }}>
          <h1>Captain Paws' Sky Adventure</h1>
          <CaptainPaws />
        </div>
      );
    }
    export default App;
    ```
3.  Run your React app (`npm start` or `yarn start`).
*/